import React, { FC, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import {
  deletionPreviewPath,
  totalDeletable,
  totalRetained,
  type ConnectedAppDeletionPreview,
  type DisconnectTarget,
} from './connectedAppDisconnect';
import './disconnect-app-modal.scss';

interface DisconnectAppModalProps {
  target: DisconnectTarget | null;
  onClose: () => void;
  assistantName: string;
}

type PreviewState =
  | { status: 'loading' }
  | { status: 'loaded'; preview: ConnectedAppDeletionPreview }
  | { status: 'error' };

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

const DisconnectAppModal: FC<DisconnectAppModalProps> = ({
  target,
  onClose,
  assistantName,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;
  const [previewState, setPreviewState] = useState<PreviewState>({
    status: 'loading',
  });
  const [deleteLearnedData, setDeleteLearnedData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const oauthId = target?.oauthId ?? null;

  useEffect(() => {
    if (oauthId == null) return undefined;
    let cancelled = false;
    setPreviewState({ status: 'loading' });
    setDeleteLearnedData(false);
    setSubmitting(false);
    setError(null);
    (async () => {
      try {
        const token = await getTokenRef.current();
        const { data } = await api.get<ConnectedAppDeletionPreview>(
          deletionPreviewPath(oauthId),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setPreviewState({ status: 'loaded', preview: data });
      } catch (err) {
        console.error('Error loading disconnect preview:', err);
        if (!cancelled) setPreviewState({ status: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [oauthId]);

  if (!target) return null;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await target.run(deleteLearnedData);
      onClose();
    } catch (err) {
      console.error('Error disconnecting account:', err);
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const preview =
    previewState.status === 'loaded' ? previewState.preview : null;
  const learned = preview?.learnedData ?? null;
  const deletable = learned ? totalDeletable(learned) : 0;
  const retained = learned ? totalRetained(learned) : 0;
  const nothingLearned = learned != null && deletable === 0 && retained === 0;

  const renderLearnedData = () => {
    if (previewState.status === 'loading') {
      return (
        <p className='learned-data-hint' data-testid='learned-data-loading'>
          Checking what {assistantName} learned from this account…
        </p>
      );
    }
    if (previewState.status === 'error') {
      return (
        <p className='learned-data-hint' data-testid='learned-data-error'>
          We couldn&rsquo;t work out what {assistantName} learned from this
          account right now. You can still disconnect it; memories will be kept.
        </p>
      );
    }
    if (nothingLearned) {
      return (
        <p className='learned-data-hint' data-testid='learned-data-empty'>
          {assistantName} hasn&rsquo;t stored any memories from this account.
        </p>
      );
    }
    return (
      <>
        <label className='learned-data-option'>
          <input
            type='checkbox'
            checked={deleteLearnedData}
            disabled={submitting || deletable === 0}
            onChange={(e) => setDeleteLearnedData(e.target.checked)}
            data-testid='delete-learned-data-checkbox'
          />
          <span className='learned-data-option-text'>
            <span className='learned-data-option-title'>
              Also delete what {assistantName} learned from this account
            </span>
            <span
              className='learned-data-option-detail'
              data-testid='learned-data-counts'
            >
              {deletable === 0
                ? 'Nothing can be removed on its own.'
                : `Removes ${plural(learned!.facts.deletable, 'fact')}, ${plural(
                    learned!.relationships.deletable,
                    'relationship',
                  )} and ${plural(learned!.entityNotes.deletable, 'note')} that came only from this account.`}
            </span>
          </span>
        </label>
        {retained > 0 && (
          <p className='learned-data-hint' data-testid='learned-data-retained'>
            {retained === 1
              ? `1 memory also comes from other sources or from what you told ${assistantName} directly, so it is kept either way.`
              : `${retained} memories also come from other sources or from what you told ${assistantName} directly, so they are kept either way.`}
          </p>
        )}
      </>
    );
  };

  return (
    <div
      className='disconnect-app-modal-overlay'
      onClick={handleClose}
      data-testid='disconnect-app-modal'
    >
      <div
        className='disconnect-app-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='disconnect-app-modal-title'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-header'>
          <p className='modal-title' id='disconnect-app-modal-title'>
            Disconnect {target.providerLabel}
          </p>
          <button
            className='close-button'
            onClick={handleClose}
            aria-label='Close'
            disabled={submitting}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        <div className='modal-body'>
          <h3 className='body-title'>Disconnect {target.label}?</h3>
          <div className='body-text'>
            <p>
              {assistantName} will stop using this account straight away. We
              delete the access we were granted
              {preview?.remoteRevoke
                ? ` and revoke it with ${target.providerLabel}`
                : ''}
              , so reconnecting later means signing in again.
            </p>
            {target.extraWarning && <p>{target.extraWarning}</p>}
          </div>

          <div className='learned-data'>{renderLearnedData()}</div>
        </div>

        <div className='modal-footer'>
          {error && <p className='disconnect-error'>{error}</p>}
          <button
            className='btn-disconnect-dismiss'
            onClick={handleClose}
            disabled={submitting}
          >
            Keep connected
          </button>
          <button
            className='btn-disconnect-confirm'
            onClick={handleConfirm}
            disabled={submitting || previewState.status === 'loading'}
            data-testid='confirm-disconnect'
          >
            {submitting
              ? 'Disconnecting…'
              : deleteLearnedData
                ? 'Disconnect and delete memories'
                : 'Disconnect'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisconnectAppModal;
